/**
 * Fractional-octave smoothing for the Frequency Response chart's continuous
 * curve — distinct from RTA's rebinning (which collapses the curve into
 * discrete bars). This is the classic REW/Smaart "1/N octave smoothing"
 * filter: each point becomes the energy-mean of every sample within a
 * ±1/(2N)-octave window centered on it, so the curve stays a continuous
 * line at the original frequency resolution, just visually smoothed.
 */
import { magToDb } from './series';

/** 'off' = no smoothing. Numbers are the fractional-octave denominator
 *  (1/3 oct, 1/6 oct, 1/12 oct, 1/24 oct) — smaller number = wider window =
 *  more smoothing. */
export type SmoothingFraction = 'off' | 1 | 3 | 6 | 12 | 24;

export const SMOOTHING_OPTIONS: { id: SmoothingFraction; label: string }[] = [
  { id: 'off', label: 'Off' },
  { id: 24, label: '1/24 oct' },
  { id: 12, label: '1/12 oct' },
  { id: 6, label: '1/6 oct' },
  { id: 3, label: '1/3 oct' },
];

/** Smooths a linear-magnitude curve and returns it still in linear-magnitude
 *  form, so callers can drop it straight into the same `mag`/`magBaseline`
 *  slot `computeFreqResponseGeometry` already accepts — smoothing is a pure
 *  pre-processing step, no change needed to the geometry function itself.
 *  `freqs`/`mag` must be the same length; returns `mag` unchanged for
 *  `'off'` or a length mismatch.
 *
 *  `freqs` must be ascending (same invariant
 *  `interpolateComplexOntoLinearGrid` in impulseResponse.ts relies on — the
 *  backend's FR response is log-spaced ascending by construction). Each
 *  point's [lo, hi] window is monotonically non-decreasing in i, so a two-
 *  pointer sweep — adding the bin that just entered the window, removing the
 *  one that just left — visits every bin at most once on each side (O(n)
 *  total) instead of every point rescanning the full array. This used to be
 *  O(n^2): at FR_N_POINTS=800 (backend/admin_optimize.py) that was ~640k
 *  comparisons per call, measured at 2-5ms — not catastrophic on its own,
 *  but it ran up to 4x per smoothing click (mag, magBaseline, and a
 *  duplicate computation in the crosshair-readout `useMemo`) and had no
 *  reason to be quadratic.
 *
 *  Deliberately an incremental running sum (+= on enter, -= on exit), NOT a
 *  prefix-sum-array difference — the power domain (10^(db/10)) spans many
 *  orders of magnitude, and subtracting two large cumulative prefix totals
 *  to recover a small window sum loses real precision (measured >1e-3
 *  relative error on some curves, not just float noise). A running sum that
 *  only ever adds/removes one bin at a time stays close in magnitude to the
 *  window's actual content instead of to the whole array's total. */
/**
 * Which domain the sliding window averages in.
 *
 * 'power' (the default, and what every chart wants) sums 10^(dB/10): the
 * physically correct way to combine SPL, since energy is what adds.
 *
 * 'db' averages the decibel values themselves. That is NOT how sound combines,
 * and it is exactly right for one job: extracting the TREND of a peaky curve.
 * A power mean is dominated by the peaks inside its window, so on a modal
 * response it sits above the middle of the ripple — measured at +0.93 dB on a
 * synthetic small room, which is a systematic bias toward "this band needs a
 * boost" for anything that treats the smoothed curve as a target to correct
 * toward (56% of the band, against 47% for a dB mean whose bias is -0.11 dB).
 * `@roomtreatment/room-eq` passes 'db' for that reason.
 */
export type SmoothingDomain = 'power' | 'db';

export function smoothFrequencyResponse(
  freqs: number[],
  mag: number[],
  fraction: SmoothingFraction,
  domain: SmoothingDomain = 'power',
): number[] {
  const n = freqs.length;
  if (fraction === 'off' || n !== mag.length || n === 0) return mag;

  const db = magToDb(mag);
  // In 'db' mode the running sum accumulates decibels directly, so the same
  // two-pointer sweep and the same window bookkeeping serve both domains —
  // only what goes into the sum, and how it comes back out, differs.
  const power = db.map((v) => (Number.isFinite(v) ? (domain === 'db' ? v : 10 ** (v / 10)) : 0));
  const finite = db.map(Number.isFinite);
  const halfLog2 = 1 / (2 * fraction); // half-window width in octaves
  const factorLo = 2 ** -halfLog2;
  const factorHi = 2 ** halfLog2;

  const out: number[] = new Array(n);
  let left = 0;
  let right = 0;
  let winPower = 0;
  let winCount = 0;
  for (let i = 0; i < n; i++) {
    const f = freqs[i]!;
    const lo = f * factorLo;
    const hi = f * factorHi;
    while (left < n && freqs[left]! < lo) {
      if (finite[left]) { winPower -= power[left]!; winCount--; }
      left++;
    }
    while (right < n && freqs[right]! <= hi) {
      if (finite[right]) { winPower += power[right]!; winCount++; }
      right++;
    }

    const mean = winCount > 0 ? winPower / winCount : null;
    const smoothedDb =
      mean == null ? db[i]! : domain === 'db' ? mean : 10 * Math.log10(mean);
    out[i] = Number.isFinite(smoothedDb)
      ? 10 ** (smoothedDb / 20 + Math.log10(2e-5)) // back to linear pressure magnitude
      : mag[i]!; // preserve NaN-mag bins (e.g. HPF floor) as-is
  }
  return out;
}
